/*
 * OpenAI Ads Measurement Pixel adapter for the Shopify storefront.
 *
 * - The loader/init lives in snippets/openai-ads-pixel.liquid and runs once.
 * - Measurement stays denied until Shopify Customer Privacy allows both marketing and analytics.
 * - No PII is collected. SDK-managed oppref/source_url/timestamps are left untouched.
 * - Commerce events listen only to `aricove:cart-add-succeeded`, dispatched after a successful
 *   Shopify Ajax Cart response by every active add/increment path in this repository.
 */
(function () {
  'use strict';

  var config = document.querySelector('script[data-openai-ads-tracker]');
  if (!config || typeof window.oaiq !== 'function') return;

  var consentGranted = false;
  var personalizationOptOut = false;
  var viewsMeasured = false;
  var serverContextStorageKey = 'aricoveOpenAIAdsContext';
  var pendingServerContext = '';

  function attribute(name) {
    return config.getAttribute(name) || '';
  }

  function integer(value) {
    if (value == null || value === '') return null;
    var number = Number(value);
    return isFinite(number) && number >= 0 ? Math.round(number) : null;
  }

  function measure(name, data) {
    if (!consentGranted) return;
    try {
      if (personalizationOptOut) window.oaiq('measure', name, data, { opt_out: true });
      else window.oaiq('measure', name, data);
    } catch (error) {
      if (window.console) console.warn('[openai-ads] ' + error.message);
    }
  }

  function rawQueryValue(name) {
    var query = window.location && window.location.search ? window.location.search.slice(1) : '';
    if (!query) return '';
    var pairs = query.split('&');
    for (var index = 0; index < pairs.length; index += 1) {
      var separator = pairs[index].indexOf('=');
      var key = separator === -1 ? pairs[index] : pairs[index].slice(0, separator);
      if (key === name) return separator === -1 ? '' : pairs[index].slice(separator + 1);
    }
    return '';
  }

  function rawCookie(name) {
    var prefix = name + '=';
    var cookies = String(document.cookie || '').split(';');
    for (var index = 0; index < cookies.length; index += 1) {
      var cookie = cookies[index].replace(/^\s+/, '');
      if (cookie.indexOf(prefix) === 0) return cookie.slice(prefix.length);
    }
    return '';
  }

  function storedServerContext() {
    try {
      return window.sessionStorage.getItem(serverContextStorageKey) || '';
    } catch (error) {
      return '';
    }
  }

  function rememberServerContext(value) {
    try {
      window.sessionStorage.setItem(serverContextStorageKey, value);
    } catch (error) {
      // Measurement still works when session storage is unavailable.
    }
  }

  function persistServerContext() {
    if (typeof window.fetch !== 'function') return;
    var oppref = rawQueryValue('oppref') || rawCookie('__oppref');
    var state = consentGranted
      ? 'allowed|' + (personalizationOptOut ? 'true' : 'false') + '|' + oppref
      : 'denied';
    var previousState = storedServerContext();
    if (previousState === state || pendingServerContext === state) return;
    if (!consentGranted && previousState.indexOf('allowed|') !== 0) return;

    var attributes = consentGranted ? {
      _openai_ads_consent: 'allowed',
      _openai_ads_opt_out: personalizationOptOut ? 'true' : 'false'
    } : {
      _openai_ads_consent: '',
      _openai_ads_opt_out: '',
      _openai_ads_oppref: ''
    };
    if (consentGranted && oppref) attributes._openai_ads_oppref = oppref;

    pendingServerContext = state;
    var shopRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    window.fetch(shopRoot.replace(/\/?$/, '/') + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      if (response.ok) rememberServerContext(state);
      pendingServerContext = '';
    }).catch(function () {
      pendingServerContext = '';
      // Attribution persistence must never interrupt storefront behavior.
    });
  }

  function measureViewsOnce() {
    if (!consentGranted || viewsMeasured) return;
    viewsMeasured = true;

    measure('page_viewed', {
      type: 'contents',
      contents: [{
        id: attribute('data-page-id') || '/',
        name: attribute('data-page-name') || document.title,
        content_type: attribute('data-page-type') || 'page'
      }]
    });

    var productId = attribute('data-product-id');
    if (!productId) return;

    var product = {
      id: productId,
      name: attribute('data-product-name'),
      content_type: 'product'
    };
    var price = integer(attribute('data-product-price'));
    var currency = attribute('data-currency').toUpperCase();
    var event = { type: 'contents', contents: [product] };
    if (price !== null && currency) {
      product.amount = price;
      product.currency = currency;
      event.amount = price;
      event.currency = currency;
    }
    measure('contents_viewed', event);
  }

  function shopifyConsentState() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy) return null;
      if (typeof privacy.marketingAllowed !== 'function') return null;
      if (typeof privacy.analyticsProcessingAllowed !== 'function') return null;
      return {
        measurement: privacy.marketingAllowed() && privacy.analyticsProcessingAllowed(),
        optOut: typeof privacy.saleOfDataAllowed === 'function' && !privacy.saleOfDataAllowed()
      };
    } catch (error) {
      return null;
    }
  }

  function syncConsent() {
    var state = shopifyConsentState();
    if (state === null) return;
    consentGranted = !!state.measurement;
    personalizationOptOut = !!state.optOut;
    try {
      window.oaiq('consent', consentGranted);
    } catch (error) {
      if (window.console) console.warn('[openai-ads] consent: ' + error.message);
      consentGranted = false;
      return;
    }
    persistServerContext();
    measureViewsOnce();
  }

  function contentFromLine(line, quantityOverride) {
    if (!line) return null;
    var id = line.variant_id != null ? line.variant_id : line.id;
    if (id == null || id === '') return null;
    var quantity = quantityOverride !== null ? quantityOverride : integer(line.quantity);
    var amount = integer(line.final_price != null ? line.final_price : line.price);
    var currency = attribute('data-currency').toUpperCase();
    var content = {
      id: String(id),
      name: line.product_title || line.title || '',
      content_type: 'product',
      quantity: quantity === null ? 1 : quantity
    };
    if (amount !== null && currency) {
      content.amount = amount;
      content.currency = currency;
    }
    return content;
  }

  function measureItemsAdded(detail) {
    syncConsent();
    if (!consentGranted) return;

    detail = detail || {};
    var response = detail.response || detail;
    var lines = response && response.items ? response.items : [response];
    var quantityOverride = lines.length === 1 ? integer(detail.quantity) : null;
    var contents = [];
    var total = 0;
    var hasTotal = true;

    for (var index = 0; index < lines.length; index += 1) {
      var content = contentFromLine(lines[index], quantityOverride);
      if (!content) continue;
      contents.push(content);
      if (content.amount == null) hasTotal = false;
      else total += content.amount * content.quantity;
    }
    if (!contents.length) return;

    var data = { type: 'contents', contents: contents };
    var currency = attribute('data-currency').toUpperCase();
    if (hasTotal && currency) {
      data.amount = total;
      data.currency = currency;
    }
    measure('items_added', data);
  }

  document.addEventListener('aricove:cart-add-succeeded', function (event) {
    measureItemsAdded(event.detail);
  });
  document.addEventListener('visitorConsentCollected', syncConsent);

  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      function (error) { if (!error) syncConsent(); }
    );
  }
  syncConsent();
  window.setTimeout(syncConsent, 1000);
})();
